import { of, Subject, Observable, defer, forkJoin, NEVER } from 'rxjs';
import { refCount, mergeMap, publishReplay, take, catchError,  tap, first, filter } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { ItemContextModel } from '../model';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash';
import { EventService } from './event.service';
import { PermissionService } from './permission.service';
import { StreamService } from './stream.service';
import { UserService } from './user.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';
import { pathToFileURL } from 'url';

export interface ItemEditContextModel {
    itemId?: string;
    sourceItemId?: string; 
    topicId?: string;
}


@Injectable({ providedIn: 'root' })
export class ItemService {
    private cachedBookmarkFetches = {};
    private path: string = '/ws/item';
    focusedItem = new Subject<ItemContextModel>();
    focusedItemEdit = new Subject<ItemEditContextModel>();
    itemClosing = new Subject<void>();
    itemContextChanged = new Subject<any>();
    bookmarkChanges = new Subject<any>();
    MAX_PAGE_SCAN_INDEX = 9;

    readonly tagDefs = {
        topics: { label: 'Classifications', key: 'topics', cls: 'bg-accent-100'},
        concepts: { label: 'Concepts', key: 'concepts', cls: 'bg-accent-200'},
        entities: { label: 'Entities', key: 'entities', cls: 'bg-accent-300'},
        socialTag: { label: 'Subjects', key: 'socialTag', cls: 'bg-accent-400'},
        keywords: { label: 'Keywords', key: 'keywords', cls: 'bg-accent-500'},
        category: { label: 'Category', key: 'category', cls: 'bg-accent-100'}
    };

    constructor(private api: ApiService,
                private eventService: EventService,
                private permissionService: PermissionService, 
                private streamService: StreamService,
                private userService: UserService,
                private dialog: MatDialog,
                private snackBar: MatSnackBar) {}

    getItem(itemId): Observable<any> {
        return this.api.get(`/ws/ua/items/${itemId}`);
    }

    getFullText(url): Observable<any> {
        return this.api.get('/ws/extract', new HttpParams().set('url', url));
    }

    toggleItemSaved(item) {
        (item.savedByUser ? this.deleteItemBookmark(item.id) : this.addItemBookmark(item.id))
        .pipe(
            first(),
            tap(() => this.bookmarkChanges.next())
        ).subscribe(() => {
            item.savedByUser = !item.savedByUser;
            this.snackBar.open(`Bookmark ${item.savedByUser ? 'Added' : 'Removed'}.`, 'OK', { duration: 2000 });
        });
    };

    searchItems(params: {page: number, includeFullDescription: boolean, sortField: string, includeReadItems?: boolean, createdAfterDateTime?: boolean}): Observable<any> {
        return this.api.get(this.path + '/search', this.api.paramify(params));
    }

    searchTenantItems(params: { page: number, 
                                includeFullDescription: boolean, 
                                sortField?: string, 
                                search?: string, 
                                includeReadItems?: boolean, 
                                includeOpenForReadingFalseStreams?: boolean, 
                                includeMarketplaceStreams?: boolean, 
                                createdAfterDateTime?: string}): Observable<any> {
        return this.api.get(this.path + '/items', this.api.paramify(params));
    }

    moreLikeThisItem(itemId: String): Observable<any> {
        return this.api.get(`/ws/item/${itemId}/moreLikeThis`);
    }

    getBookmarkedItems(params: {page: number, includeFullDescription: boolean, sortField: string, includeReadItems?: boolean, createdAfterDateTime?: boolean}): Observable<any> {
        const key = String(params.page) + String(params.includeReadItems) + String(params.createdAfterDateTime) + params.sortField + String(params.includeFullDescription);
        if (!this.cachedBookmarkFetches[key]) {
            const cacheTime = 1000 * 60 * 5; // Five minutes
            this.cachedBookmarkFetches[key] = defer(() => this.api.get('/ws/user/bookmarks/items', this.api.paramify(params)).pipe(catchError(e => NEVER))).pipe(publishReplay(1, cacheTime),refCount(),take(1),);
        }
        return this.cachedBookmarkFetches[key];
    };

    markItemRead(itemId: string): Observable<any> {
        const currentUser = this.userService.getCurrentUser();
        return currentUser ? this.api.post(`${this.path}/${itemId}/users`, { userId: currentUser.id }) : of(null);
    };

    toggleLikedBy(item): Observable<any> {
        return item.likedByUser ? 
        this.removeItemLikedBy(item)
        .pipe(tap(() => {
            item.likedByUser = undefined;
            if (item.likesCount) {
                item.likesCount--;
            }
        })) :
        this.addItemLikedBy(item)
        .pipe(tap(() => {
            item.likedByUser = true;
            item.likesCount++;
        }));
    }

    addItemLikedBy(item): Observable<any> {
        return this.api.post(`${this.path}/${item.id}/likes`, {}, { responseType: 'text'}).pipe(tap(() => 
        this.eventService.postEvent('item_like_clicked', 
                                    Object.assign(
                                        { viewItemSession: this.eventService.viewSessionId }, 
                                        this.eventService.formatItem(item))
                                    )));
    }

    removeItemLikedBy(item): Observable<any> {
        return this.api.delete(`${this.path}/${item.id}/likes`, { responseType: 'text'}).pipe(tap(() => 
        this.eventService.postEvent('item_unlike_clicked', 
                                    Object.assign(
                                        { viewItemSession: this.eventService.viewSessionId }, 
                                        this.eventService.formatItem(item)
                                    ))));
    }

    addItemBookmark(itemId: string): Observable<any> {
        return this.api.post('/ws/user/bookmarks/items', [itemId], {responseType: 'text'})
            .pipe(tap(() => this.cachedBookmarkFetches = {}));
    }

    deleteItemBookmark(itemId: string): Observable<any> {
        return this.api.delete(`/ws/user/bookmarks/items/${itemId}`, {responseType: 'text'})
            .pipe(tap(() => this.cachedBookmarkFetches = {}));
    }

    deleteItem(streamId: string, itemId: string): Observable<any> {
        return this.api.delete(`/ws/streams/${streamId}/items/${itemId}`);
    }

    isReadable(topic, item) {
        const isTopicViewer = topic ? this.permissionService.currentUserHasPermission({ action: 'VIEW_STREAM', streamId: topic.id, streamOwnerId: topic.ownerId}) : false;
        const isSourceViewer = item ? this.permissionService.currentUserHasPermission({ action: 'VIEW_STREAM', streamId: item.streamSourceId}) : false;
        return (!topic && isSourceViewer) || (topic && isTopicViewer) || (!topic || topic && (topic.openForReading)) && item && item.sourceStreamOpenForReading;
    };

    patchItem(streamId: string, itemId: string, patch: any): Observable<any> {
        return this.api.patch(`/ws/item/${itemId}?streamId=${streamId}`, patch);
    }

    private emailShareLink(item: any): string {
        const itemLink = `${window.location.protocol}//${window.location.host}/client/browse/categories/uncategorized/topics/${item.streamId}/items/${item.id}`;
        const itemDirectLink = '  ' + (item.link === '#' || !item.link.length ? itemLink : encodeURIComponent(item.link));
        const linkStart = `mailto:?subject=Recommended: ${item.title}&body=`;
        const contents = _.truncate((item.cleanDescription || item.shortDescription).replace(/&/g, 'and'), { length: 1000 - (linkStart.length + itemDirectLink.length)});
        return linkStart + contents + itemDirectLink;
    };

    searchItemsWithSettings(settings, params): Observable<any> {
        return this.api.post('/ws/item/settingsSearch', settings, { params: this.api.paramify(params) });
    }

    getItemOembed(link): Observable<any> {
        return this.api.get('/ws/twitter/tweet', this.api.paramify({url: link}));
    }

    shareItemByEmail(item: any, stream: any): void {
        forkJoin([
            this.getItem(item.id).pipe(first()),
            (stream ? of(stream) : this.streamService.getStream(item.streamId)).pipe(first())
        ])
        .subscribe(res => {
            const fetchedItem = res[0].item ? res[0].item : res[0];
            const topic = res[1];
            this.eventService.postEvent('item_share_email_clicked', Object.assign(
                { viewItemSession: this.eventService.viewSessionId }, 
                this.eventService.formatItem(item), 
                this.eventService.formatStream(topic))
            );
            this.patchItem(topic ? topic.id : fetchedItem.streamId, fetchedItem.id, { sharedBy: this.userService.getCurrentUser().id })
            .pipe(
                tap(() => window.open(this.emailShareLink(fetchedItem), '_blank')),
                // tap(() => window.location.href = this.emailShareLink(fetchedItem)),
                first()
            ).subscribe();
        });
    };

    searchItemsInStream(opts): Observable<any> {
        return this.api.get(`/ws/ua/streams/${opts.streamId}/items`, this.api.paramify(opts));
    }

    searchTwitter(query): Observable<any> {
        return this.api.get('/ws/twitter', this.api.paramify({search: query}));
    }

    searchBing(term, settings): Observable<any> {
        return this.api.post('/ws/stream_source/bing/preview', { search: term, type: settings.type });
    }

    searchPubmed(term): Observable<any> {
        return this.api.post('/ws/stream_source/pubmed/preview', { search: term });
    }

    searchScopus(search: string, apiKey: string, insttoken: string): Observable<any> {
        return this.api.post('/ws/stream_source/scopus/preview', { search, apiKey, insttoken });
    }

    searchClinicaltrials(term): Observable<any> {
        return this.api.post('/ws/stream_source/clinicaltrials/preview', { search: term });
    }

    searchDiffbot(search: string, apiToken: string): Observable<any> {
        return this.api.post('/ws/stream_source/diffbot/preview', { search, apiToken });
    }

    toggleCurationItem(item, topic): Observable<any> {
        if (item.inCurationItems) {
            item.inCurationItems = !item.inCurationItems;
            return this.removeCurationItem(topic.id, item.id);
        } else {
            if (topic.curationItemIds?.length === 10) {
                return this.dialog.open(ConfirmationDialogComponent, { data: {
                    title: 'Training set full',
                    content: 'Training sets are best kept to 5 or fewer items, but cannot go above 10.',
                    actionLabel: 'OK',
                    hideCancel: true
                }}).afterClosed();
            } else if (topic.curationItemIds?.length > 5) {
                return this.dialog.open(ConfirmationDialogComponent, { data: {
                    title: 'Training set full',
                    content: 'Training sets are best kept to 5 or fewer items.',
                }}).afterClosed()
                .pipe(
                    filter(doIt => doIt),
                    tap(() => item.inCurationItems = !item.inCurationItems),
                    mergeMap(() => this.addCurationItem(topic.id, item.id))
                );
            } else {
                item.inCurationItems = !item.inCurationItems;
                return this.addCurationItem(topic.id, item.id);                
            }
        }
    }

    addCurationItem(streamId: string, itemId: string): Observable<any> {
        return this.api.post(`/ws/streams/${streamId}/curationItems`, [itemId]);
    }

    removeCurationItem(streamId: string, itemId: string): Observable<any> {
        return this.api.delete(`/ws/streams/${streamId}/curationItems/${itemId}`);
    }

    childClearedPath(): string {
        const exemptPaths = ['digest/recent', 'digest/search'];
        const pathSegments = window.location.pathname.split('/').slice(2);
        if (pathSegments[pathSegments.length -2] === 'topics') {
            return `${pathSegments.join('/')}${window.location.search}`;
        } else if (exemptPaths.includes(pathSegments.join('/')) || pathSegments.length === 1) {
            return `/${pathSegments.join('/')}${window.location.search}`;
        } else {
            const spliceNum = pathSegments[pathSegments.length - 1] === 'post' ? 1 : 2;
            pathSegments.splice(pathSegments.length - spliceNum, spliceNum);
            return `/${pathSegments.join('/')}${window.location.search}`;
        }
    }
}
